import { Box, Typography } from '@mui/material';
import { memo, useMemo, useState } from 'react';
import Chart from 'react-apexcharts';

import { useTranslation } from 'react-i18next';

import { theme } from '../../../theme';
import { lineChartStyle } from './style';

const COLOR_GREEN = theme.palette.dashBoar.mintGreen;
const COLOR_ORANGE = theme.palette.dashBoar.softPeach;
const COLOR_RED = theme.palette.dashBoar.lightCoral;
const COLOR_TEXT_GREEN = theme.palette.dashBoar.darkmintGreen;
const COLOR_TEXT_ORANGE = theme.palette.dashBoar.darksoftPeach;
const COLOR_TEXT_RED = theme.palette.dashBoar.darkCoral;

const defaultGraphColors = {
  text: {
    positive: COLOR_TEXT_GREEN,
    neutural: COLOR_TEXT_ORANGE,
    negative: COLOR_TEXT_RED,
  },
  series: {
    positive: COLOR_GREEN,
    neutural: COLOR_ORANGE,
    negative: COLOR_RED,
  },
};

export const LineChart = memo((props) => {
  const {
    values,
    colors,
    height,
    width,
    small,
    customOptions,
    NoDataFallback = null,
  } = props;
  const { t } = useTranslation();
  const [graphColors, setGraphColors] = useState({
    text: { ...defaultGraphColors.text, ...colors?.text },
    series: { ...defaultGraphColors.series, ...colors?.series },
  });

  const seriesData = (Array.isArray(values) ? values : []).map((item) =>
    parseInt(item),
  );

  /**
   * Calculate series's delta
   */
  const delta = useMemo(
    () => seriesData[seriesData.length - 1] - seriesData[0],
    [seriesData],
  );

  /**
   * Returns one of three colors based on the delta in the series, note: positive delta is bad.
   */
  const seriesTextColor = useMemo(() => {
    if (seriesData.length <= 1) return graphColors.text.negative;
    if (delta > 0) return graphColors.text.negative;
    if (delta < 0) return graphColors.text.positive;

    return graphColors.text.neutural;
  }, [delta, graphColors, seriesData]);

  /**
   * Returns one of three colors based on the delta in the series, note: positive delta is bad.
   */
  const seriesColor = useMemo(() => {
    if (seriesData.length <= 1) return '#fff';
    if (delta > 0) return graphColors.series.negative;
    if (delta < 0) return graphColors.series.positive;

    return graphColors.series.neutural;
  }, [delta, graphColors, seriesData]);

  /**
   * Gets delta series text
   */
  const seriesDeltaText = useMemo(() => {
    if (!seriesData || seriesData.length <= 1) return '0';
    return delta > 0 ? `+${delta}` : `${delta}`;
  }, [delta, seriesData]);

  const { lineChart } = lineChartStyle(seriesColor, seriesTextColor);

  /**
   * Gets chart options
   */
  const options = useMemo(() => {
    const optionData = {
      chart: {
        stacked: true,
        type: 'area',
      },
      plotOptions: {
        area: {
          paddingBottom: 0,
        },
      },
      stroke: {
        width: [2, 2],
        curve: 'smooth',
      },
      title: {
        text: 'Sud scores',
        align: 'left',
      },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 0.1,
          inverseColors: true,
          opacityFrom: 0.55,
          opacityTo: 0.45,
          stops: [20, 100],
        },
      },
      grid: {
        show: true,
        borderColor: theme.palette.neutral.grey10,
        yaxis: {
          lines: {
            show: true,
          },
        },
        padding: {
          left: 12,
          right: 10,
          top: 0,
          bottom: 0,
        },
      },
      markers: {
        strokeColors: theme.palette.neutral.grey70,
        strokeWidth: 1.5,
        strokeOpacity: 0.7,
        size: 3,
        hover: {
          size: 5,
        },
      },
      xaxis: {
        type: 'numeric',
        tickAmount: seriesData.length - 1,
        floating: false,
        decimalsInFloat: 0,
        position: 'bottom',
        labels: {
          show: true,
          showDuplicates: false,
          style: {
            fontFamily: `${theme.typography.mainFontFamily}, Arial, sans-serif`,
            cssClass: 'apexcharts-xaxis-label',
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        crosshairs: {
          show: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      yaxis: {
        show: true,
        showAlways: true,
        labels: {
          show: true,
          showDuplicates: false,
          style: {
            fontFamily: `${theme.typography.mainFontFamily}, Arial, sans-serif`,
            cssClass: 'apexcharts-yaxis-label',
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        tooltip: {
          enabled: false,
          style: {
            fontFamily: theme.typography.mainFontFamily,
          },
        },
      },
      colors: [seriesColor],
      series: [
        {
          name: 'area',
          type: 'area',
          data: seriesData,
        },
      ],
    };
    if (small) {
      optionData.grid = {
        padding: {
          left: 0,
          right: 0,
          top: 5,
        },
      };
      optionData.chart.stacked = false;
      optionData.chart.sparkline = {
        enabled: true,
      };
      optionData.fill = {
        type: 'gradient',
        gradient: {
          shadeIntensity: 0.1,
          inverseColors: true,
          opacityFrom: 0.55,
          opacityTo: 0.45,
          stops: [20, 100, 20, 100],
        },
      };
      optionData.colors = [seriesColor];
      optionData.legend = {
        show: false,
      };
      optionData.yaxis = {
        min: 0,
        max: 11,
        show: false,
      };
      optionData.tooltip = {
        intersect: false,
        followCursor: true,
        custom({ series, seriesIndex, dataPointIndex, w }) {
          const value = series[seriesIndex][dataPointIndex];
          const delta = value - series[seriesIndex][dataPointIndex - 1];

          let output = '';
          if (!isNaN(delta)) {
            output = ` (${delta > 0 ? '+' : ''}${delta})`;
          }

          return `<div class="tooltip-sparkline"><span> ${value}${output}</span></div>`;
        },
      };
      delete optionData.title;
    }
    return { ...optionData, ...customOptions };
  }, [customOptions, seriesColor, seriesData, small]);

  /**
   * Return's fallback component if chart data is not exists
   */
  if (!seriesData.length) return NoDataFallback;

  return (
    <Box sx={lineChart} className='graph-wrapper'>
      {small && (
        <Box
          className='label'
          sx={{
            borderColor: seriesTextColor,
            color: seriesTextColor,
            background: seriesColor,
          }}
        >
          <Typography>{seriesDeltaText}</Typography>
        </Box>
      )}
      <Chart
        options={options}
        series={options.series}
        height={height || '300'}
        width={width || '100%'}
      />
    </Box>
  );
});
